import React, { useContext, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { AuthContext } from '../Firebase/AuthProvider';
import { BUSINESSES_COLLECTION, BUSINESS_PCT, SERVICE_PCT } from '../Data/offers';

// Business accounts: boutiques, showrooms and tailors that take orders from
// their own clients (website docs/business-plan.md). They apply from the
// website's account Settings (Businesses/{email or phone}, status
// 'pending'). Only this page verifies, turns down or pauses them. A verified
// business gets a fixed BUSINESS_PCT off every piece and no codes or coins;
// its clients pay the website price plus a SERVICE_PCT service charge. The
// server (api/place-order.js) checks each order against this document.

const STATE = {
  pending: { label: 'Waiting for review', color: '#8a5a00', bg: '#fdf3dc' },
  verified: { label: 'Verified', color: '#187044', bg: '#e8f4ec' },
  rejected: { label: 'Not approved', color: '#9b2c2c', bg: '#fdecea' },
  paused: { label: 'Paused', color: '#7a6750', bg: '#f3eee6' },
};
const FILTERS = [['pending', 'Waiting'], ['verified', 'Verified'], ['rejected', 'Not approved'], ['paused', 'Paused'], ['all', 'All']];

const when = (v) => {
  const d = v?.toDate ? v.toDate() : v ? new Date(v) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '';
};
const ms = (v) => { const d = v?.toDate ? v.toDate() : v ? new Date(v) : null; return d && !Number.isNaN(d.getTime()) ? d.getTime() : 0; };

const card = { background: '#fff', border: '1px solid #e2d7c6', borderRadius: 4, padding: 16 };
const input = { height: 36, padding: '0 10px', border: '1px solid #d7cbb7', borderRadius: 3, fontSize: '0.9rem', background: '#fff' };
const hint = { fontSize: '0.72rem', fontWeight: 500, color: '#7a6750' };
const btn = (dark, color) => ({
  height: 36, padding: '0 14px', borderRadius: 3, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem',
  border: dark ? `1px solid ${color || '#3e4a2f'}` : '1px solid #d7cbb7', background: dark ? (color || '#3e4a2f') : '#fff', color: dark ? '#fff' : (color || '#3e4a2f'),
});

function Detail({ k, v }) {
  if (!v) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '130px minmax(0, 1fr)', gap: 10, fontSize: '0.85rem', padding: '4px 0' }}>
      <span style={{ color: '#7a6750', fontWeight: 600 }}>{k}</span><span style={{ overflowWrap: 'anywhere' }}>{v}</span>
    </div>
  );
}

// How the shop's name reads on the website when nobody has set it: the
// name as applied, with each word capitalised if it was typed all lowercase.
const autoName = (n) => { const raw = String(n || '').trim(); return raw === raw.toLowerCase() ? raw.replace(/\b\p{L}/gu, (m) => m.toUpperCase()) : raw; };

function BusinessRow({ b, onSave, onName }) {
  const [note, setNote] = useState(b.reviewNote || '');
  const [shown, setShown] = useState(b.displayName || '');
  const [busy, setBusy] = useState('');
  const st = STATE[b.status] || STATE.pending;

  const act = async (status) => {
    if (status !== 'verified' && !note.trim()) { window.alert('Write why, so the customer knows what to do.'); return; }
    setBusy(status);
    try {
      await onSave(b, status === 'verified' ? { status, reviewNote: '' } : { status, reviewNote: note.trim() });
    } finally { setBusy(''); }
  };
  const link = b.website && (/^https?:\/\//.test(b.website) ? b.website : `https://${b.website}`);

  return (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: '1.05rem' }}>{b.businessName || '(no name)'}</strong>
        <span style={{ background: st.bg, color: st.color, padding: '2px 8px', borderRadius: 3, fontSize: '0.7rem', fontWeight: 800 }}>{st.label}</span>
        {b.status === 'verified' && <span style={{ background: '#000', color: '#ddb56c', padding: '3px 8px', fontWeight: 800, fontSize: '0.8rem' }}>{BUSINESS_PCT}% off</span>}
        <span style={{ ...hint, marginLeft: 'auto' }}>Applied {when(b.appliedAt)}{b.updatedAt && ms(b.updatedAt) - ms(b.appliedAt) > 60000 ? ` · edited ${when(b.updatedAt)}` : ''}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '0 24px' }}>
        <div>
          <Detail k="Type" v={b.businessType} />
          <Detail k="GSTIN" v={b.gstin || 'Not given'} />
          <Detail k="City" v={[b.city, b.state].filter(Boolean).join(', ')} />
          <Detail k="Website" v={link ? <a href={link} target="_blank" rel="noopener noreferrer">{b.website}</a> : ''} />
        </div>
        <div>
          <Detail k="Contact" v={b.contactName} />
          <Detail k="Mobile" v={b.phone ? <a href={`https://wa.me/91${b.phone}`} target="_blank" rel="noopener noreferrer">+91 {b.phone} (WhatsApp)</a> : ''} />
          <Detail k="Account" v={b.account || b.id} />
          <Detail k="Orders" v={[b.orders, b.volume].filter(Boolean).join(' · ')} />
        </div>
      </div>
      {b.note && <div style={{ background: '#f6f0e6', padding: '8px 12px', fontSize: '0.85rem', borderLeft: '3px solid #9d7130' }}>{b.note}</div>}
      {/* The name on the website's nav bar and in showroom mode. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', fontWeight: 700, color: '#4a4034', flex: '1 1 260px' }}>
          Name on the website (nav bar and showroom)
          <input style={input} value={shown} maxLength={40} onChange={(e) => setShown(e.target.value)} placeholder={autoName(b.businessName)} />
        </label>
        <button type="button" style={btn(false)} disabled={!!busy || shown.trim() === (b.displayName || '')} onClick={async () => { setBusy('name'); try { await onName(b, shown.trim()); } finally { setBusy(''); } }}>{busy === 'name' ? 'Saving…' : 'Save name'}</button>
        <span style={hint}>Shows as “{shown.trim() || autoName(b.businessName)}”</span>
      </div>
      {b.reviewedAt && <span style={hint}>Last decision {when(b.reviewedAt)}{b.reviewedBy ? ` by ${b.reviewedBy}` : ''}{b.reviewNote ? ` · "${b.reviewNote}"` : ''}</span>}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', paddingTop: 12, borderTop: '1px dashed #e2d7c6' }}>
        {b.status !== 'verified' && (
          <button type="button" style={btn(true, '#187044')} disabled={!!busy} onClick={() => act('verified')}>
            {busy === 'verified' ? 'Saving…' : b.status === 'paused' ? `Turn back on (${BUSINESS_PCT}% off)` : `Verify (${BUSINESS_PCT}% off)`}
          </button>
        )}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.75rem', fontWeight: 700, color: '#4a4034', flex: '1 1 240px' }}>
          Note to the customer (needed to turn down or pause)
          <input style={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Please add a GSTIN or a shop photo on WhatsApp" />
        </label>
        {b.status !== 'rejected' && <button type="button" style={btn(false, '#9b2c2c')} disabled={!!busy} onClick={() => act('rejected')}>{busy === 'rejected' ? 'Saving…' : 'Turn down'}</button>}
        {b.status === 'verified' && <button type="button" style={btn(false)} disabled={!!busy} onClick={() => act('paused')}>{busy === 'paused' ? 'Saving…' : 'Pause'}</button>}
      </div>
    </div>
  );
}

function BusinessesPage() {
  const { user } = useContext(AuthContext) || {};
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => db.collection(BUSINESSES_COLLECTION).onSnapshot(
    (snap) => { const out = []; snap.forEach((d) => out.push({ id: d.id, ...d.data() })); setRows(out); },
    (e) => { console.error('businesses', e); setRows([]); setMessage('Could not read business accounts. Check that the Firestore rules allow the Businesses collection.'); },
  ), []);

  const list = useMemo(() => (rows || []).slice().sort((a, b) => ms(b.updatedAt || b.appliedAt) - ms(a.updatedAt || a.appliedAt)), [rows]);
  const q = search.trim().toLowerCase();
  const shown = list.filter((b) => (filter === 'all' || (b.status || 'pending') === filter)
    && (!q || [b.businessName, b.contactName, b.phone, b.account, b.gstin, b.city].some((v) => String(v || '').toLowerCase().includes(q))));
  const count = (st) => list.filter((b) => (b.status || 'pending') === st).length;

  const saveName = async (b, displayName) => {
    try {
      await db.collection(BUSINESSES_COLLECTION).doc(b.id).set({ displayName }, { merge: true });
      setMessage(`${b.businessName}: the website now shows “${displayName || autoName(b.businessName)}”.`);
    } catch (e) {
      console.error('business name', e);
      setMessage(`Could not save the name. (${e?.message || e})`);
    }
  };
  const save = async (b, patch) => {
    try {
      await db.collection(BUSINESSES_COLLECTION).doc(b.id).set({ ...patch, reviewedAt: new Date(), reviewedBy: user?.email || '' }, { merge: true });
      setMessage(`${b.businessName}: ${STATE[patch.status].label.toLowerCase()}. The customer sees it in their account now.`);
    } catch (e) {
      console.error('business save', e);
      const denied = String(e?.code || e?.message || '').includes('permission');
      setMessage(denied ? 'Could not save: the Firestore rules do not allow the Businesses collection yet.' : `Could not save. Please try again. (${e?.message || e})`);
    }
  };

  if (rows === null) return <div style={{ padding: 20 }}>Loading business accounts…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '4px 0 40px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10 }}>
        {[['Waiting', count('pending'), '#8a5a00'], ['Verified', count('verified'), '#187044'], ['Not approved', count('rejected'), '#9b2c2c'], ['Paused', count('paused'), '#7a6750']].map(([t, v, color]) => (
          <div key={t} style={{ ...card, padding: '11px 12px' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 900, color }}>{v}</div>
            <div style={{ marginTop: 4, fontSize: '0.64rem', color: '#6A7079', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, background: '#f6f0e6', borderColor: '#d7cbb7', fontSize: '0.85rem', color: '#4a4034' }}>
        Verify a business after a call or a look at its shop, Instagram or GSTIN. A verified business gets {BUSINESS_PCT}% off every piece and nothing else: no coupons, coins, prepaid 5% or cash on delivery. Its clients pay the website price plus a {SERVICE_PCT}% service charge to the boutique, and its orders reach you with each client's name.
      </div>

      {message && <div style={{ ...card, background: '#f6f0e6', borderColor: '#9d7130', fontWeight: 600, fontSize: '0.88rem' }}>{message}</div>}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTERS.map(([k, t]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} style={{ ...btn(filter === k), height: 32 }}>
              {t}{k !== 'all' && count(k) ? ` (${count(k)})` : ''}
            </button>
          ))}
        </div>
        <input style={{ ...input, width: 260 }} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, GSTIN, city" />
      </div>

      {shown.length === 0 && <div style={{ ...card, color: '#7a6750' }}>{list.length ? 'Nothing here.' : 'No business has applied yet. They apply from Account → Settings on the website.'}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.map((b) => <BusinessRow key={`${b.id}-${b.status}`} b={b} onSave={save} onName={saveName} />)}
      </div>
    </div>
  );
}

export default BusinessesPage;
